<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <breadcrumbs
      hide-client
      :custom-breadcrumbs="[
        $t('activities.breadcrumbElLabel'),
        $t('activities.myActivities.breadcrumbElLabel')
      ]"
    />
    <div
      v-if="!isParent"
      class="row q-col-gutter-md"
    >
      <div>
        <q-btn
          :label="$t('activities.buttonAddSessionActivity')"
          :disable="!networkOnline && !offlineMode"
          icon="event"
          color="primary"
          @click="onAddActivity"
        />
      </div>
      <div v-if="hasActiveSessionActivity">
        <q-btn
          :label="$t('activities.buttonFinishSessionActivity')"
          :loading="isBindingActiveSessionActivities || isProcessing"
          :icon="activeSessionActivityIcon"
          :disable="!networkOnline && !offlineMode"
          color="negative"
          @click="onFinishActivity"
        />
      </div>
      <div v-else>
        <q-btn
          :label="$t('activities.buttonStartSessionActivity')"
          :loading="isBindingActiveSessionActivities || isProcessing"
          :disable="!networkOnline && !offlineMode"
          icon="timer"
          color="positive"
          @click="onStartActivity"
        />
      </div>
    </div>
    <session-activities-table
      ref="sessionActivitiesTable"
      :all-programs="allPrograms"
      :fixed-filters="fixedFilters"
      :default-filters="defaultFilters"
      :default-columns="[
        'status','client','startTimestamp', 'duration', 'sessionName'
      ]"
      hide-pagination-label
      :show-context-menu="userCanSeeMenu"
      disable-export-with-data
      local-storage-prop="ownActivities"
      :virtual-scroll="$q.platform.is.desktop"
      bordered
      is-on-my-activities-page
      @click="onViewSession"
      @edit="onEditSession"
    />
  </q-page>
</template>

<script>
import { filterPropertyTypes } from 'boot/globals';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import StartSessionActivityDialog from 'components/SessionActivities/Modals/StartSessionActivityDialog';
import SessionActivityDialog from 'components/Sessions/Modals/SessionActivityDialog';
import SessionCardDialog from 'components/Sessions/Modals/SessionCardDialog';
import SessionActivitiesTable from 'components/Sessions/SessionActivitiesTable';
import { Timestamp } from 'firebase/firestore';
import { find, get, head, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { useAppointments } from 'src/composables/appointments';
import { sessionActivitiesTableSource, useActivityNavigation } from 'src/composables/useActivityNavigation';
import { useSessionActivities } from 'src/composables/sessionActivities';
import { useStaffActivityAppointmentLink } from 'src/composables/staffActivityAppointmentLink';
import { SESSION_ACTIVITY_TYPES } from 'src/constants/sessionActivities';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import DateRanges from 'src/mixins/DateRanges';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import { auth } from 'src/store/auth';
import { geolocation } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';

export default {
  name: 'Activities',

  components: { Breadcrumbs, SessionActivitiesTable },

  mixins: [DateRanges, Interactions, SessionActivitiesMixin],

  setup() {
    const { checkUserCanStartSessionActivity } = useSessionActivities();
    const { promptStaffActivityAppointmentLink } = useStaffActivityAppointmentLink();
    const { completeLinkedAppointment } = useAppointments();

    return {
      checkUserCanStartSessionActivity,
      promptStaffActivityAppointmentLink,
      completeLinkedAppointment,
      activityNav: useActivityNavigation(),
    };
  },

  data() {
    return {
      isProcessing: false,

      isBindingActiveSessionActivities: false,

      activeSessionActivities: null,

      recentlyViewed: null,
    };
  },

  computed: {
    ...mapState(auth, ['isParent', 'activityTypesSettings', 'hasTherapistCredentials']),
    ...mapState(programs, ['allPrograms']),
    ...mapState(network, ['networkOnline', 'offlineMode']),

    userCanSeeMenu() {
      return this.hasTherapistCredentials;
    }, // userCanSeeMenu()

    fixedFilters() {
      return {
        userName: {
          id: uid(),
          filterProperty: 'user',
          filterPropertyType: filterPropertyTypes.User,
          filterStatements: [
            {
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: [this.userDetails?.userId],
              label: this.userDetails?.name,
            },
          ],
        },
      };
    }, // fixedFilters()

    defaultFilters() {
      const defaultDateRange = 'prev-7-days';
      // // Add a default filter
      const fromDate = Timestamp
        .fromDate(find(this.periodPresets, { id: defaultDateRange }).fromTimestamp);
      const toDate = Timestamp
        .fromDate(find(this.periodPresets, { id: defaultDateRange }).toTimestamp);
      const { label } = find(this.periodPresets, { id: defaultDateRange });

      return {
        startTimestamp: {
          id: uid(),
          filterProperty: 'startTimestamp',
          filterPropertyType: filterPropertyTypes.Date,
          isPersistent: true,
          filterStatements: [
            {
              fromDate,
              toDate,
              filterDateItems: [],
              filterCriteriaType: defaultDateRange,
              customDataId: null,
              filterNegative: false,
              filteredItems: [],
              label,
            },
          ],
        },
      };
    }, // defaultFilters()

    activeSessionActivity() {
      return head(this.activeSessionActivities);
    },

    hasActiveSessionActivity() {
      return !isEmpty(this.activeSessionActivities);
    },

    activeSessionActivityIcon() {
      if (!this.activeSessionActivity) {
        return null;
      }
      return get(this.activityTypesSettings, [this.activeSessionActivity.type, 'icon'], 'event');
    },

  },

  created() {
    this.saveViewType('sessionActivities');

    this.activityNav.configure({
      source: sessionActivitiesTableSource(() => this.$refs.sessionActivitiesTable),
      openers: {
        activity: (row, controller) => this.openActivityDialog(row, controller),
        session: (row, controller) => this.openSessionDialog(row, controller),
      },
    });

    this.bindActiveSessionActivities();

    const sessionId = get(this.$route, ['query', 'session'], null);
    const sessionActivityId = get(this.$route, ['query', 'activity'], null);

    if (sessionId || sessionActivityId) {
      this.onViewSession(sessionId, sessionActivityId);
    }
  },

  methods: {
    ...mapActions(geolocation, ['refreshGeolocation', 'executeGeolocationCallback']),

    onAddActivity() {
      const startTimestamp = Timestamp.now();
      const endTimestamp = Timestamp.fromMillis(startTimestamp.toMillis() + 30 * 60 * 1000);

      const { session, supervision, form } = SESSION_ACTIVITY_TYPES;
      const excludedOptions = { session, supervision, form };

      const sessionActivity = {
        startTimestamp,
        endTimestamp,
        status: 'finished',
        type: null,
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        client: null,
        clinic: this.userDetails.clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId: null,

        appointment: null,
        session: null,
        sessionName: '',

        createdBy: this.userDetails.userId,

        version: 2,

        isDeleted: false,
      };

      // Show session activity timing dialog
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
        title: this.$t('activities.buttonAddSessionActivity'),
        excludedOptions,
      }, async (newSessionActivity) => {
        const sessionActivitiesDb = new SessionActivitiesDB();

        try {
          const createdSessionActivity = await sessionActivitiesDb.create({
            ...sessionActivity,
            ...newSessionActivity,
            client: null,
            clinic: this.userDetails.clinic,
          });

          if (createdSessionActivity.id) {
            await this.promptStaffActivityAppointmentLink(createdSessionActivity);

            this.showSessionActivityDialog(createdSessionActivity.id);
            if (this?.$refs?.sessionActivitiesTable) {
              this.$refs.sessionActivitiesTable.refreshAll();
            }
          }
        } catch (err) {
          this.notifyNegative(err.message);
        }
      });
    },

    async onStartActivity() {
      this.isProcessing = true;
      if (!await this.checkUserCanStartSessionActivity(null)) {
        this.isProcessing = false;
        return;
      }

      this.refreshGeolocation(false);
      const startTimestamp = Timestamp.now();
      let endTimestamp = null;
      if (startTimestamp instanceof Timestamp) {
        const duration = 60 * 60;
        endTimestamp = Timestamp.fromMillis(startTimestamp.toMillis() + duration * 1000);
      }

      const { session, supervision, form } = SESSION_ACTIVITY_TYPES;
      const excludedOptions = { session, supervision, form };

      const sessionActivity = {
        startTimestamp,
        endTimestamp,
        status: 'in-progress',
        type: null,
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        client: null,
        clinic: this.userDetails.clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId: null,

        appointment: null,
        session: null,
        sessionName: '',

        createdBy: this.userDetails.userId,

        version: 2,

        isDeleted: false,
      };
      this.isProcessing = false;
      // Show session activity timing dialog
      this.openComponentDialog(StartSessionActivityDialog, {
        sessionActivity,
        title: this.$t('activities.buttonStartSessionActivity'),
        excludedOptions,
      }, async (newSessionActivity) => {
        this.isProcessing = true;
        const sessionActivitiesDb = new SessionActivitiesDB();

        try {
          const createdSessionActivity = await sessionActivitiesDb.create({
            ...sessionActivity,
            ...newSessionActivity,
            client: null,
            clinic: this.userDetails.clinic,
          });

          await this.promptStaffActivityAppointmentLink(createdSessionActivity);

          if (this?.$refs?.sessionActivitiesTable) {
            this.$refs.sessionActivitiesTable.refreshAll();
          }
        } catch (err) {
          this.notifyNegative(err.message);
        } finally {
          this.isProcessing = false;
        }
      });
    }, // onStartActivity()

    onFinishActivity() {
      if (!this.hasActiveSessionActivity) {
        return;
      }
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t(
          'activities.confirmFinishActivity',
        ),
        async () => {
          this.isProcessing = true;
          this.refreshGeolocation(false);
          const sessionActivityId = this.activeSessionActivity.id;
          // `activeSessionActivity` comes from a live query for in-progress activities, so the
          // status write below empties it. Read the appointment id while it is still there.
          const linkedAppointmentId = this.activeSessionActivity.appointment;

          const sessionActivitiesDb = new SessionActivitiesDB();

          const updatedData = {
            status: await this.applySessionActivityFinishedStatus(sessionActivityId, 'Activities.onFinishActivity'),
            endTimestamp: Timestamp.now(),
          };

          await sessionActivitiesDb.set(updatedData, sessionActivityId);
          await this.completeLinkedAppointment(linkedAppointmentId);

          this.executeGeolocationCallback((endGeolocation) => {
            if (!this || !endGeolocation) {
              // The dialog was destroyed while waiting for the geolocation
              return;
            }
            sessionActivitiesDb.set({
              endGeolocation,
            }, sessionActivityId);
          });

          this.isProcessing = false;
          this.openComponentDialog(SessionActivityDialog, {
            sessionActivityId,
            applyImmediately: true,
          }, () => {
            if (this?.$refs?.sessionActivitiesTable) {
              this.$refs.sessionActivitiesTable.refresh(sessionActivityId);
            }
          }, () => {
            if (this?.$refs?.sessionActivitiesTable) {
              this.$refs.sessionActivitiesTable.refresh(sessionActivityId);
            }
          });
        },
      );
    },

    onViewSession(sessionId, sessionActivityId, sessionActivity) {
      // Deep links only carry ids, so synthesise a row in that case.
      const row = sessionActivity || { id: sessionActivityId, session: sessionId || null };
      this.activityNav.start(row);
    }, // onViewSession()

    openActivityDialog(row, navController) {
      return this.openComponentDialog(
        SessionActivityDialog,
        { sessionActivityId: row.id, navController },
        async () => {
          if (this?.$refs?.sessionActivitiesTable) {
            this.$refs.sessionActivitiesTable.refresh(navController?.currentId || row.id);
          }
        },
      );
    }, // openActivityDialog()

    async openSessionDialog(row, navController) {
      const sessionsDb = new SessionsDB();
      let session = null;
      let dialogData = null;

      this.$q.loading.show();
      try {
        session = await sessionsDb.read(row.session);
        if (session?.isDeleted) {
          session = null;
        }
        if (session) {
          dialogData = await this.loadSessionDialogData(session);
        }
      } catch (err) {
        this.notifyNegative(err.message);
        return null;
      } finally {
        this.$q.loading.hide();
      }

      if (!session) {
        // Only show the activity if the activity does not have a session attached to it
        return this.openActivityDialog(row, navController);
      }

      return this.openComponentDialog(SessionCardDialog, {
        allPrograms: dialogData.allPrograms,
        session,
        client: dialogData.client,
        openSessionActivity: row.id,
        navController,
      }, async () => {
        if (this?.$refs?.sessionActivitiesTable) {
          await this.$refs.sessionActivitiesTable.refreshSession(row.session);
        }
      });
    }, // openSessionDialog()

    async onEditSession(sessionId, sessionActivityId, sessionActivity) {
      const clientId = sessionActivity?.client;
      if (!clientId) {
        return;
      }

      const session = await this.loadSessionDoc(sessionId);
      if (!session) {
        return this.alertDialog(
          this.$t('sessions.modals.session.dialogTitleDeleted'),
          this.$t('sessions.modals.sessionActivity.dialogMessageSessionDeleted'),
        );
      }

      window.open(`/#/history/${sessionId}/edit?client=${clientId}`);
    }, // onEditSession()

    async bindActiveSessionActivities() {
      this.isBindingActiveSessionActivities = true;
      const sessionActivitiesDb = new SessionActivitiesDB();
      try {
        await sessionActivitiesDb.bindAll(
          this,
          'activeSessionActivities',
          [
            ['status', '==', 'in-progress'],
            ['client', '==', null],
            ['user', '==', this.userDetails.userId],
            ['isDeleted', '==', false],
          ],
          null,
          null,
          1,
        );
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.isBindingActiveSessionActivities = false;
      }
    },

    async showSessionActivityDialog(sessionActivityId) {
      this.openComponentDialog(
        SessionActivityDialog,
        { sessionActivityId },
        async () => {
          if (this?.$refs?.sessionActivitiesTable) {
            this.$refs.sessionActivitiesTable.refresh(sessionActivityId);
          }
        },
      );
    },

    saveViewType(viewType) {
      localStorage.setItem('sessionViewType', viewType);
    }, // saveViewType()

  },

};
</script>
<style lang="scss" scoped>

.view-type-toggle {
  border: 1px solid $primary;
}

</style>
